import { AnyExtension, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { toPersistedInlineRichTextHtml, toPersistedRichTextHtml } from 'shared/utils/richText';
import { RichTextVariant } from './types';

const LINE_BREAKS = /\s*[\r\n]+\s*/g;

/** Enter and Shift+Enter add nothing: the value is one line. */
const SingleLine = Extension.create({
  name: 'singleLine',
  addKeyboardShortcuts: () => ({
    Enter: () => true,
    'Shift-Enter': () => true,
    'Mod-Enter': () => true,
  }),
});

/** Inline formatting only; the block tools of StarterKit are off. */
const inlineStarterKit = StarterKit.configure({
  blockquote: false,
  bulletList: false,
  code: false,
  codeBlock: false,
  hardBreak: false,
  heading: false,
  horizontalRule: false,
  link: false,
  listItem: false,
  listKeymap: false,
  orderedList: false,
});

const blockStarterKit = StarterKit.configure({
  link: {
    openOnClick: false,
    HTMLAttributes: {
      target: '_blank',
      rel: 'noopener noreferrer',
    },
  },
});

/** What makes a variant of the editor; the components read it instead of checking the variant. */
interface RichTextVariantBehaviour {
  extensions: AnyExtension[];
  /** The HTML saved and compared: what the backend stores for the variant. */
  toPersistedHtml: (html: string) => string;
  /** Lists, links, code and line breaks in the toolbar; formatting and colour are always there. */
  hasBlockTools: boolean;
  /** A `count / max` counter under the field. */
  showsCharacterCount: boolean;
  /** Pasted content becomes one line. */
  isOneLine: boolean;
}

export const RICH_TEXT_VARIANTS: Record<RichTextVariant, RichTextVariantBehaviour> = {
  block: {
    extensions: [blockStarterKit],
    toPersistedHtml: toPersistedRichTextHtml,
    hasBlockTools: true,
    showsCharacterCount: true,
    isOneLine: false,
  },
  inline: {
    extensions: [inlineStarterKit, SingleLine],
    toPersistedHtml: toPersistedInlineRichTextHtml,
    hasBlockTools: false,
    showsCharacterCount: false,
    isOneLine: true,
  },
};

interface EditorExtensionsOptions {
  variant: RichTextVariant;
  maxLength: number;
  placeholder?: string;
}

export function createEditorExtensions({ variant, maxLength, placeholder }: EditorExtensionsOptions): AnyExtension[] {
  return [
    ...RICH_TEXT_VARIANTS[variant].extensions,
    // Text colour (PT-7250): stored as `<span style="color:#RRGGBB">`.
    TextStyle,
    Color,
    CharacterCount.configure({ limit: maxLength }),
    ...(placeholder ? [Placeholder.configure({ placeholder, emptyEditorClass: 'is-editor-empty' })] : []),
  ];
}

/** Set by TipTap on HTML copied from one of our editors. */
const COPIED_FROM_EDITOR = 'data-pm-slice';
/** Blocks whose text joins the line of a one-line value, separated by a space. */
const BLOCKS_OF_ONE_LINE = 'p, div, br, li, ul, ol, h1, h2, h3, h4, h5, h6, blockquote, pre, table, thead, tbody, tr, td, th';

export function toOneLine(text: string): string {
  return text.replace(LINE_BREAKS, ' ');
}

/**
 * Pasted HTML before the editor reads it (its own rules still apply: a span
 * styled bold stays bold):
 * - text from elsewhere (Word, a web page) loses its colours: they are its
 *   default black or link blue, not a colour the user picked; a copy between
 *   our editors keeps them;
 * - a one-line field gets one line, with a space where a block ended.
 */
export function preparePastedHtml(html: string, variant: RichTextVariant): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  const { content } = template;
  if (!html.includes(COPIED_FROM_EDITOR)) {
    content.querySelectorAll<HTMLElement>('[style]').forEach((element) => element.style.removeProperty('color'));
  }
  if (!RICH_TEXT_VARIANTS[variant].isOneLine) {
    return template.innerHTML;
  }
  content.querySelectorAll(BLOCKS_OF_ONE_LINE).forEach((block) => block.replaceWith(...Array.from(block.childNodes), ' '));
  return `<p>${template.innerHTML.trim()}</p>`;
}

interface RichTextFieldIdsInput {
  dataQa?: string;
  fieldName?: string;
}

/** DOM ids of a field: its wrapper, and its label that names the editable text for screen readers. */
export function richTextFieldIds({ dataQa, fieldName }: RichTextFieldIdsInput): { fieldId: string; labelElementId: string } {
  const fieldId = dataQa ?? fieldName ?? 'rich-text-editor';
  return { fieldId, labelElementId: `${fieldId}-label` };
}
